import React, {useState, useEffect, useRef} from "react";
import Menu from "../../../components/layout/menu/index";
import TableConfiguration from "../../../components/tables/configuration/index";
import SaveButton from "../../../components/forms/saveButton";
import CancelButton from "../../../components/forms/cancelButton";
import ModalSuccess from "../../../components/shared/modals/modalSuccess";
import FilterInput from "../../../components/filters/filterInput";
import MultiSelectSearch from "../../../components/filters/multiSelectSearch";
import ResetFilters from "../../../components/filters/resetFilters";
import { getUsers} from "../../../client/users";
import { getPermissionsList } from "../../../client/permissions";
import { useAuth } from "../../../context/auth";

export default function Configuration() {
  
  const {refreshPermissions} = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [hasChanges, setHasChanges] = useState(false); 
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [showStatusFilter, setShowStatusFilter] = useState(false);

  const [users, setUsers] = useState([]);
  const [modules, setModules] = useState([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [isLoadingModules, setIsLoadingModules] = useState(false);
  const [activeFilters, setActiveFilters] = useState(false);

  const [appliedFilters, setAppliedFilters] = useState({
    userIds: [],
    permissionIds: []
  });

  const [pendingUser, setPendingUser] = useState(null);
  const [pendingModule, setPendingModule] = useState(null);

  const tableRef = useRef();

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500); 

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  useEffect(() => {
    fetchUsers();
    fetchModules();
  }, []);

  const fetchUsers = async () => {
    setIsLoadingUsers(true);
    try {
      const response = await getUsers();
      if (response.data && Array.isArray(response.data)) {
        const userOptions = response.data.map(user => ({
          value: user.id,
          label: user.name
        }));
        setUsers(userOptions);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setIsLoadingUsers(false);
    }
  };

  const fetchModules = async () => {
    setIsLoadingModules(true);
    try {
      const response = await getPermissionsList();
      if (response.data && Array.isArray(response.data)) {
        const moduleOptions = response.data.map(module => ({
          value: module.id,
          label: module.text
        }));
        setModules(moduleOptions);
      }
    } catch (error) {
      console.error('Error fetching modules:', error);
    } finally {
      setIsLoadingModules(false);
    }
  };

  useEffect(() => {
    const hasActiveFilters = appliedFilters.userIds.length > 0 || appliedFilters.permissionIds.length > 0;
    setActiveFilters(hasActiveFilters);
  }, [appliedFilters]);
  const handleUserChange = (selectedOptions) => {
    setPendingUser(selectedOptions ? selectedOptions.map(opt => opt.value) : []);
  };

  const handleModuleChange = (selectedOptions) => {
    setPendingModule(selectedOptions ? selectedOptions.map(opt => opt.value) : []);
  };


  const handleApplyFilters = () => {
    const newFilters = {
      userIds: pendingUser || [],
      permissionIds: pendingModule || []
    };
    
    setAppliedFilters(newFilters);
    setShowStatusFilter(false);
  };

  const handleResetFilters = () => {
    setPendingUser([]);
    setPendingModule([]);
    setAppliedFilters({
      userIds: [],
      permissionIds: []
    });
    setSearchTerm(""); 
  };


  useEffect(() => {
    const checkChanges = () => {
      if (tableRef.current) {
        setHasChanges(tableRef.current.hasChanges);
      }
    };

    const interval = setInterval(checkChanges, 500);
    return () => clearInterval(interval);
  }, []);

  const handleSave = async () => {
    try {
      if (tableRef.current) {
        const saveResult = await tableRef.current.handleSave();
        if (saveResult) {
          await refreshPermissions();
          setShowSuccessModal(true);
        }
      }
    } catch (error) {
      console.error('Error al guardar:', error);
    }
  };

  return (
    <div className="min-h-screen">
      <Menu isMenuOpen={isMenuOpen} toggleMenu={toggleMenu} className="transition-all duration-300" />
      <div className={`flex-1 pt-[67px] transition-all duration-300  bg-[#F7F8F9] w-full z-50 min-h-screen ${isMenuOpen ? "xl:pl-[20.7%] lg:pl-[23.7%] pl-0" : "pl-0"} `}>
        <div className="sm:pt-8 pt-6 pb-24 px-[3%] min-h-screen flex flex-col justify-between">
          <div className="flex flex-col gap-3 ">   
              <div className="flex flex-col items-center justify-between gap-2 md:flex-row ">
                <h1 className="text-blue font-itcbold text-2xl md:text-[32px]">
                  Administrar accesos
                </h1>
                <div className="flex flex-col items-start gap-2 sm:items-center sm:flex-row">
                  <FilterInput
                    placeholder="Buscar"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <div className="flex gap-2">
                    <div className="relative">
                      <button
                        onClick={() => setShowStatusFilter(!showStatusFilter)}
                        className={`flex items-center justify-center h-[40px] px-4 p-2 border-[1px] rounded-lg 
                          ${activeFilters 
                            ? 'bg-blue-light text-white border-blue-light' 
                            : 'bg-transparent text-[#6B7280] border-blue-light hover:bg-gray-100'
                          }`}
                      >
                        <p className="text-[14px] leading-none font-itcbook">Filtrar por</p>
                      </button>
  
                      {showStatusFilter && (
                        <div className="absolute right-auto z-10 w-64 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg sm:right-0 sm:w-80 ">
                          <div className="flex flex-col gap-3 p-5">
                            <div className="flex flex-col gap-1">
                              <p className="text-sm md:text-base font-itcbook text-blue">Usuario:</p>
                              <MultiSelectSearch
                                name="userFilter"
                                value={users.filter(user => pendingUser?.includes(user.value))}
                                onChange={handleUserChange}
                                options={users}
                                placeholder="Seleccione usuarios"
                                isLoading={isLoadingUsers}
                                isDisabled={isLoadingUsers}
                                noOptionsMessage="No se encontraron usuarios"
                              />
                            </div>
                            <div className="flex flex-col gap-1">
                              <p className="text-sm md:text-base font-itcbook text-blue">Módulo:</p>
                              <MultiSelectSearch
                                name="moduleFilter"
                                value={modules.filter(module => pendingModule?.includes(module.value))}
                                onChange={handleModuleChange}
                                options={modules}
                                placeholder="Seleccione módulos"
                                isLoading={isLoadingModules}
                                isDisabled={isLoadingModules}
                                noOptionsMessage="No se encontraron módulos"
                              />
                            </div>
                            <button
                              onClick={handleApplyFilters}
                              className="px-4 py-2 underline transition-colors rounded-lg text-md text-blue font-itcbook ">
                                Aplicar
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
  
                    <ResetFilters
                      borderColor="border-blue-light"
                      textColor="text-[#6B7280]"
                      font="font-itcbook"
                      onClick={handleResetFilters}
                    />
                  </div>
                </div>
              </div>

              <TableConfiguration
                ref={tableRef}
                searchTerm={debouncedSearchTerm}
                appliedFilters={appliedFilters}
              />      
          </div>
          <div className={`fixed flex  gap-3  transform -translate-x-1/2 bottom-4 left-1/2 
            ${isMenuOpen ? "xl:pl-[20.7%] lg:pl-[23.7%] pl-0" : "pl-0"}`}> 
                  
            <SaveButton
              onClick={handleSave}
              disabled={!hasChanges}
              width="w-[150px] sm:w-[250px]"
              height="h-[50px] sm:h-[64px]"
            />
                  
            <CancelButton
              onClick={() => {
                if (tableRef.current) {
                  tableRef.current.handleCancel();
                }
              }}
              disabled={!hasChanges}
              bg="bg-[#F7F8F9]"
              width="w-[150px] sm:w-[250px]"
              height="h-[50px] sm:h-[64px]"
            />
          </div>
        </div>
      </div>

      <ModalSuccess 
        showModalSuccess={showSuccessModal}
        closeModalSuccess={() => setShowSuccessModal(false)}
        fill="green"
        text="¡Sus accesos fueron guardados con éxito!"
      />

    </div>
  );
}